import React, { useCallback, useState, useEffect } from "react";
import { Loading } from "../../../../../components/Loading/index.jsx";
import styles from "./styles.module.scss";
import { BrComerceApiService } from "../../../../../services/BrComerceApiService.js";
import Swal from "sweetalert2";
import { PiCopySimple } from "react-icons/pi";
import { handleAddressChange } from "../../../../../utils/handleAddressChange.js";
import { cnpjValidation } from "../../../../../utils/CnpjValidation.js";
import FormatDocument from "../../../../../utils/FormatDocument.js";

const onlyDigits = value => String(value ?? "").replace(/\D/g, "");

const isValidCnpj = value =>
  cnpjValidation({ cnpj: onlyDigits(value), emptyCnpj: "", incorrectCnpj: "" }).status === "ok";

const Intelipost = () => {
  const api = new BrComerceApiService();

  const [loading, setLoading] = useState(false);

  const [apiKey, setApiKey] = useState("");
  const [intelipostApiKey, setIntelipostApiKey] = useState("");

  const [copiedUrl, setCopiedUrl] = useState(false);

  const [sedexCode, setSedexCode] = useState("");
  const [pacCode, setPacCode] = useState("");
  const [pacminiCode, setPacminiCode] = useState("");
  const [ownDeliveryCode, setOwnDeliveryCode] = useState("");

  const [activeInsurance, setActiveInsurance] = useState(false);

  const [activeCreateOrder, setActiveCreateOrder] = useState(true);

  // CNPJs de remetente aceitos pela conta - texto unico separado por virgula
  const [senderCnpjs, setSenderCnpjs] = useState("");

  const [fulfillOnCollect, setFulfillOnCollect] = useState(false);

  const [senderName, setSenderName] = useState("");
  const [senderNameOK, setSenderNameOK] = useState("");
  const [senderNameError, setSenderNameError] = useState("");

  const [address, setAddress] = useState({
    zipOK: "",
    zipError: "",
    zip: "",
    street: "",
    streetOK: "",
    streetError: "",
    complement: "",
    complementOK: "",
    complementError: "",
    number: "",
    numberOK: "",
    neighborhood: "",
    neighborhoodOK: "",
    neighborhoodError: "",
    uf: "",
    ufOK: "",
    ufError: "",
    city: "",
    cityOK: "",
    cityError: ""
  })

  const baseUrl = `${process.env.REACT_APP_API_HOST}/intelipost/webhook/order/create`;

  const handleApiKey = (e) => {
    setApiKey(e?.target?.value)
  };

  const handleIntelipostApiKey = (e) => {
    setIntelipostApiKey(e?.target?.value)
  };

  const handleCopyWebhookUrl = () => {
    navigator.clipboard.writeText(baseUrl);
    setCopiedUrl(true);
  };

  const handleSenderCnpjs = useCallback(e => {
    setSenderCnpjs(e?.target?.value);
  }, []);

  // Ao sair do campo, reformata os CNPJs validos para "00.000.000/0000-00, ..."
  const handleSenderCnpjsBlur = useCallback(() => {
    setSenderCnpjs(prev =>
      prev
        .split(",")
        .map(token => onlyDigits(token))
        .filter(digits => digits.length > 0)
        .map(digits => (digits.length === 14 ? FormatDocument(digits) : digits))
        .join(", ")
    );
  }, []);

  const parsedCnpjs = senderCnpjs
    .split(",")
    .map(token => onlyDigits(token))
    .filter(digits => digits.length > 0);

  const invalidCnpjs = parsedCnpjs.filter(digits => !isValidCnpj(digits));

  const handleSubmit = useCallback(
    async (key, value) => {
      api.makeHttpRequest({
        url: "/intelipost/config",
        method: "POST",
        data: {
          key: key,
          value: value
        }
      });
    },
    [api]
  );

  const sendData = async () => {

    const addressComplete = {
      zip: address.zip,
      street: address.street,
      number: address.number,
      complement: address.complement,
      district: address.neighborhood,
      city: address.city,
      state: address.uf
    };

    if (invalidCnpjs.length) {
      return Swal.fire({
        icon: "warning",
        title: "CNPJ invalido",
        text: "Verifique os CNPJs de remetente informados. Separe multiplos CNPJs por virgula."
      });
    }

    try {
      await Promise.all([
        handleSubmit("API_KEY", apiKey),
        handleSubmit("INTELIPOST_API_KEY", intelipostApiKey),
        handleSubmit("SEDEX_OPTION_CODE", sedexCode),
        handleSubmit("PAC_OPTION_CODE", pacCode),
        handleSubmit("PACMINI_OPTION_CODE", pacminiCode),
        handleSubmit("OWN_DELIVERY_OPTION_CODE", ownDeliveryCode),
        activeInsurance
          ? handleSubmit("IS_ACTIVE_INSURANCE", "true")
          : handleSubmit("IS_ACTIVE_INSURANCE", "false"),
        activeCreateOrder
          ? handleSubmit("IS_INACTIVE_ORDER_CREATE", "false")
          : handleSubmit("IS_INACTIVE_ORDER_CREATE", "true"),
        handleSubmit("SENDER_DEFAULT_NAME", senderName),
        handleSubmit("SENDER_DEFAULT_ADDRESS", JSON.stringify(addressComplete)),
        handleSubmit("SENDER_CNPJ", [...new Set(parsedCnpjs)].join(",")),
        fulfillOnCollect
          ? handleSubmit("IS_ACTIVE_FULFILL_ON_COLLECT", "true")
          : handleSubmit("IS_ACTIVE_FULFILL_ON_COLLECT", "false")
      ]);

      Swal.fire({
        title: "Sucesso!",
        text: "Configurações cadastradas com sucesso.",
        icon: "success",
        showCancelButton: false,
        confirmButtonText: "Ok"
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Ocorreu um erro!",
        text: "Não foi possível fazer a deleção do envio."
      });
    }
  };


  const loadingConfig = useCallback(async () => {
    await Promise.all([
      api
        .makeHttpRequest({
          method: "GET",
          url: `/intelipost/config/API_KEY`
        })
        .then(res => {
          if (res) {
            setApiKey(res);
          }
        }),
      api
        .makeHttpRequest({
          method: "GET",
          url: `/intelipost/config/INTELIPOST_API_KEY`
        })
        .then(res => {
          if (res) {
            setIntelipostApiKey(res);
          }
        }),
      api
        .makeHttpRequest({
          method: "GET",
          url: `/intelipost/config/SEDEX_OPTION_CODE`
        })
        .then(sedexCode => {
          if (sedexCode) {
            setSedexCode(sedexCode);
          }
        }),
      api
        .makeHttpRequest({
          method: "GET",
          url: `/intelipost/config/PAC_OPTION_CODE`
        })
        .then(pacCode => {
          if (pacCode) {
            setPacCode(pacCode);
          }
        }),
      api
        .makeHttpRequest({
          method: "GET",
          url: `/intelipost/config/PACMINI_OPTION_CODE`
        })
        .then(pacminiCode => {
          if (pacminiCode) {
            setPacminiCode(pacminiCode);
          }
        }),
      api
        .makeHttpRequest({
          method: "GET",
          url: `/intelipost/config/OWN_DELIVERY_OPTION_CODE`
        })
        .then(ownDeliveryCode => {
          if (ownDeliveryCode) {
            setOwnDeliveryCode(ownDeliveryCode);
          }
        }),
      api
        .makeHttpRequest({
          method: "GET",
          url: `/intelipost/config/IS_ACTIVE_INSURANCE`
        })
        .then(active => {
          if (active === "true") {
            setActiveInsurance(true);
          }
        }),
      api
        .makeHttpRequest({
          method: "GET",
          url: `/intelipost/config/IS_INACTIVE_ORDER_CREATE`
        })
        .then(inactive => {
          if (inactive === "true") {
            setActiveCreateOrder(false);
          }
        }),
      api
        .makeHttpRequest({
          method: "GET",
          url: `/intelipost/config/SENDER_CNPJ`
        })
        .then(res => {
          if (res) {
            const unique = [
              ...new Set(
                String(res)
                  .split(",")
                  .map(token => onlyDigits(token))
                  .filter(digits => digits.length > 0)
              )
            ];

            setSenderCnpjs(
              unique
                .map(digits => (digits.length === 14 ? FormatDocument(digits) : digits))
                .join(", ")
            );
          }
        }),
      api
        .makeHttpRequest({
          method: "GET",
          url: `/intelipost/config/IS_ACTIVE_FULFILL_ON_COLLECT`
        })
        .then(active => {
          if (active === "true") {
            setFulfillOnCollect(true);
          }
        }),
      api
        .makeHttpRequest({
          method: "GET",
          url: `/intelipost/config/SENDER_DEFAULT_NAME`
        })
        .then(name => {
          if (name) {
            setSenderName(name);
            setSenderNameOK("ok");
          }
        }),
      api
        .makeHttpRequest({
          method: "GET",
          url: `/intelipost/config/SENDER_DEFAULT_ADDRESS`
        })
        .then(res => {
          if (res) {
            const address = JSON.parse(res || "[]");
            const { zip, street, complement, city, state, number, district } = address;
            setAddress({
              zip: zip ?? "",
              zipOK: zip ? "ok" : "",
              street: street ?? "",
              streetOK: street ? "ok" : "",
              complement: complement ?? "",
              complementOK: complement ? "ok" : "",
              city: city ?? "",
              cityOK: city ? "ok" : "",
              uf: state ?? "",
              ufOK: state ? "ok" : "",
              number: number ?? "",
              numberOK: number ? "ok" : "",
              neighborhood: district ?? "",
              neighborhoodOK: district ? "ok" : ""
            })
          }
        })
    ]);
  }, [api]);

  useEffect(() => {
    if (!copiedUrl) return;

    const timeout = setTimeout(() => {
      setCopiedUrl(false);
    }, 2500);

    return () => clearTimeout(timeout);
  }, [copiedUrl]);

  useEffect(() => {
    setLoading(true);

    loadingConfig()
      .then(() => {
        console.log("Configurações ok");
      })
      .catch(() => {
        Swal.fire({
          title: "Erro!",
          text: "Não foi possível carregar as informações da loja Intelipost.",
          icon: "error"
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleSedexCode = useCallback(e => {
    setSedexCode(e?.target?.value)
  }, []);

  const handlePacCode = useCallback(e => {
    setPacCode(e?.target?.value)

  }, []);

  const handlePacminiCode = useCallback(e => {
    setPacminiCode(e?.target?.value)

  }, []);

  const handleOwnDeliveryCode = useCallback(e => {
    setOwnDeliveryCode(e?.target?.value)
  }, []);

  const handleInputSenderName = useCallback(e => {
    const name = e.currentTarget.value;

    if (name.length < 1) {
      setSenderName("");
      setSenderNameError("");
      setSenderNameOK("vazio");
    } else if (name.length < 2) {
      setSenderName(name);
      setSenderNameError("Nome do remetente deve ter pelo menos 2 caracteres");
      setSenderNameOK("notOk");
    } else {
      setSenderName(name);
      setSenderNameError("");
      setSenderNameOK("ok");
    }
  }, []);

  const validatedAddressError = () => {
    const result =
      address.cityOK === "ok" &&
      address.ufOK === "ok" &&
      address.zipOK === "ok" &&
      address.neighborhoodOK === "ok" &&
      address.streetOK === "ok" &&
      address.numberOK !== "notOk" &&
      address.complementOK !== "notOk" &&
      senderNameOK === "ok";
    return result;
  }


  useEffect(() => {
    window.setPageTitle("Integrações | Intelipost");
  }, []);

  return (
    <>
      <Loading loading={loading} />
      <div className={styles.container}>
        <div className={styles.content}>
          {
            (!apiKey || !intelipostApiKey) && !loading &&
            <div className={styles.warningBanner}>
              <p>
                Entre em contato com a <b>Intelipost</b> para obter as informações necessárias para a integração.
              </p>
            </div>
          }
          <div className={styles.contentHeader}>
            <h2>Configurações</h2>
          </div>
          <div className={styles.contentBody}>
            <section>
              <div>
                <h4>
                  URL para envio dos pedidos:
                </h4>
                <div className={styles.contentSection}>
                  <div
                    className={styles.inputCustomUrl}
                    onClick={handleCopyWebhookUrl}
                  >
                    <input
                      type="text"
                      placeholder=""
                      value={baseUrl}
                      disabled
                    />
                    <PiCopySimple />
                    <div>
                      <p className={styles.textClipped}>
                        {copiedUrl
                          ? "Copiado com sucesso!"
                          : ""}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4>Chave API para Logística:</h4>
                <div className={styles.contentSection}>
                  <div className={styles.inputApiKey}>
                    <input
                      type="text"
                      name="apiKey"
                      value={apiKey}
                      onChange={handleApiKey}
                      placeholder="Insira a chave da API para logística"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4>Chave API Intelipost:</h4>
                <div className={styles.contentSection}>
                  <div className={styles.inputApiKey}>
                    <input
                      type="text"
                      name="intelipostApiKey"
                      value={intelipostApiKey}
                      onChange={handleIntelipostApiKey}
                      placeholder="Insira a chave da API Intelipost"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4>CNPJs de remetente:</h4>
                <p style={{ marginTop: "16px" }}>
                  Informe os CNPJs que a Intelipost envia como origem dos pedidos,
                  separados por vírgula. Todos eles serão associados a esta conta.
                  <br />
                  Se ficar em branco, continua valendo o CNPJ cadastrado no seu perfil.
                </p>
                <div className={styles.contentSection}>
                  <div className={styles.divContainer}>
                    <div className={styles.input} role={invalidCnpjs.length ? "notOk" : ""}>
                      <textarea
                        name="senderCnpjs"
                        value={senderCnpjs}
                        onChange={handleSenderCnpjs}
                        onBlur={handleSenderCnpjsBlur}
                        placeholder="00.000.000/0000-00, 11.111.111/1111-11"
                        rows={3}
                        autoComplete="off"
                      />
                    </div>
                    {invalidCnpjs.length > 0 && (
                      <p className={styles.error}>
                        CNPJ(s) inválido(s):{" "}
                        {invalidCnpjs
                          .map(digits => (digits.length === 14 ? FormatDocument(digits) : digits))
                          .join(", ")}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.contentSectionServices}>
                <h4>Códigos de Serviço:</h4>
                <div className={styles.contentSection}>
                  <p>Expresso: </p>
                  <div className={styles.input}>
                    <input
                      type="text"
                      name="sedexCode"
                      value={sedexCode}
                      onChange={handleSedexCode}
                      placeholder="Digite o código do Expresso"
                    />
                  </div>
                </div>
                <div className={styles.contentSection}>
                  <p>Econômico: </p>
                  <div className={styles.input}>
                    <input
                      type="text"
                      name="pacCode"
                      value={pacCode}
                      onChange={handlePacCode}
                      placeholder="Digite o código do Econômico"
                    />
                  </div>
                </div>
                <div className={styles.contentSection}>
                  <p>Super Econômico: </p>
                  <div className={styles.input}>
                    <input
                      type="text"
                      name="pacminiCode"
                      value={pacminiCode}
                      onChange={handlePacminiCode}
                      placeholder="Digite o código do Super Econômico"
                    />
                  </div>
                </div>
                <div className={styles.contentSection}>
                  <p>Super Expresso: </p>
                  <div className={styles.input}>
                    <input
                      type="text"
                      name="ownDeliveryCode"
                      value={ownDeliveryCode}
                      onChange={handleOwnDeliveryCode}
                      placeholder="Digite o código do Super Expresso"
                    />
                  </div>
                </div>
              </div>

              <div className={styles.sectionTitle}>
                <p style={{ marginTop: 0 }}>
                  Informe o nome de remetente que deve constar nas etiquetas
                  de envio geradas pela plataforma.
                </p>
                <div className={styles.contentSection}>
                  <div className={styles.divContainer}>
                    <div className={styles.input} role={senderNameOK}>
                      <input
                        onChange={e => handleInputSenderName(e)}
                        value={senderName}
                        placeholder="Digite o nome do remetente"
                        type="text"
                        name="senderName"
                      />
                    </div>
                    {senderNameError && (
                      <p className={styles.error}>{senderNameError}</p>
                    )}
                  </div>
                </div>
              </div>

              <form>
                <div className={`${styles.inputContainer}`}>
                  <p style={{ marginTop: 0, marginBottom: "16px" }}>
                    Informe os dados de endereço do remetente
                  </p>
                </div>

                <div className={`${styles.inputContainer} `}>
                  <div className={` ${styles.divContainer}`}>
                    <div className={` ${styles.inputContainerSmCep} `}>
                      <div className={styles.input} role={address.zipOK}>
                        <input
                          type="text"
                          name="cep"
                          placeholder="Digite o CEP"
                          onChange={(e) => handleAddressChange("zip", e.target.value, setAddress, setLoading)}
                          value={address.zip}
                          maxLength={8}
                        />
                        <a
                          href="https://buscacepinter.correios.com.br/app/endereco/index.php"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.cepLink}
                        >
                          Não sei o CEP
                        </a>
                      </div>
                    </div>
                  </div>
                  {address.zipError && (
                    <p className={styles.error}>{address.zipError}</p>
                  )}
                </div>

                <div
                  className={`${styles.inputContainer} ${styles.inputContainerAddress}`}
                >
                  <div
                    className={` ${styles.divContainer} ${styles.inputContainerMd}`}
                  >
                    <div className={` ${styles.input} `} role={address.streetOK}>
                      <input
                        onChange={(e) => handleAddressChange("street", e.target.value, setAddress)}
                        value={address.street}
                        placeholder="Digite o endereço"
                        type="text"
                        name="address"
                      />
                    </div>
                    {address.streetError && (
                      <p className={styles.error}>{address.streetError}</p>
                    )}
                  </div>

                  <div className={` ${styles.divContainer}`}>
                    <div className={styles.input} role={address.numberOK}>
                      <input
                        onChange={(e) => handleAddressChange("number", e.target.value, setAddress)}
                        value={address.number}
                        placeholder="Digite o número"
                        type="text"
                        name="addressNumber"
                      />
                    </div>
                    {address.numberError && (
                      <p className={styles.error}>{address.numberError}</p>
                    )}
                  </div>
                </div>

                <div
                  className={`${styles.inputContainer} ${styles.inputContainerAddress}`}
                >
                  <div className={` ${styles.divContainer}`}>
                    <div className={styles.input} role={address.complementOK}>
                      <input
                        onChange={(e) => handleAddressChange("complement", e.target.value, setAddress)}
                        value={address.complement}
                        placeholder="Digite o complemento"
                        type="text"
                        name="addressObs"
                      />
                    </div>
                    {address.complementError && (
                      <p className={styles.error}>{address.complementError}</p>
                    )}
                  </div>

                  <div className={` ${styles.divContainer}`}>
                    <div className={styles.input} role={address.neighborhoodOK}>
                      <input
                        onChange={(e) => handleAddressChange("neighborhood", e.target.value, setAddress)}
                        value={address.neighborhood}
                        placeholder="Digite o bairro"
                        type="text"
                        name="addressNeighborhood"
                      />
                    </div>
                    {address.neighborhoodError && (
                      <p className={styles.error}>{address.neighborhoodError}</p>
                    )}
                  </div>
                </div>

                <div
                  className={`${styles.inputContainer} ${styles.inputContainerAddress}`}
                >
                  <div className={styles.divContainer}>
                    <div className={` ${styles.divContainer}`}>
                      <div className={styles.input} role={address.cityOK}>
                        <input
                          onChange={() => { }}
                          placeholder="Cidade"
                          value={address.city}
                          type="text"
                          name="city"
                        />
                      </div>
                    </div>
                  </div>

                  <div className={styles.divContainer}>
                    <div className={styles.input} role={address.ufOK}>
                      <input
                        onChange={() => { }}
                        placeholder="Estado"
                        value={address.uf}
                        type="text"
                        name="uf"
                      />
                    </div>
                  </div>
                </div>
              </form>

              <div>
                <h4>Criação de pedido</h4>
                <p style={{ marginTop: "16px" }}>
                  Abaixo é possível ativar ou desatitvar a criação de pedidos.
                  <br />
                  Serviços como cotação funcionarão normalmente
                </p>
                <div>
                  <div className={styles.contentSection}>
                    <div className={styles.switchOption}>
                      <label className={styles.switch}>
                        <input
                          type="checkbox"
                          onChange={event =>
                            setActiveCreateOrder(event.target.checked)
                          }
                          checked={activeCreateOrder}
                        />
                        <span
                          className={`${styles.slider} ${styles.round}`}
                        ></span>
                      </label>
                      {activeCreateOrder ? "Ativado" : "Desativado"}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4>Envio da SRO na criação da coleta</h4>
                <p style={{ marginTop: "16px" }}>
                  Quando ativado, assim que a coleta é criada enviamos para a Intelipost
                  o código de rastreio e o status "Coletado".
                  <br />
                  Desativado, a SRO continua sendo enviada apenas na expedição.
                </p>
                <div>
                  <div className={styles.contentSection}>
                    <div className={styles.switchOption}>
                      <label className={styles.switch}>
                        <input
                          type="checkbox"
                          onChange={event =>
                            setFulfillOnCollect(event.target.checked)
                          }
                          checked={fulfillOnCollect}
                        />
                        <span
                          className={`${styles.slider} ${styles.round}`}
                        ></span>
                      </label>
                      {fulfillOnCollect ? "Ativado" : "Desativado"}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4>Configuração de Seguro</h4>
                <p style={{ marginTop: "16px" }}>
                  Abaixo é possível ativar ou desatitvar a cotação de frete com
                  Seguro. Essa opção é válida para todos os fretes <br /> que
                  são cotados.
                </p>
                <div className={styles.contentSection}>
                  <div className={styles.switchOption}>
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        onChange={event =>
                          setActiveInsurance(event.target.checked)
                        }
                        checked={activeInsurance}
                        disabled={!apiKey}
                      />
                      <span
                        className={`${styles.slider} ${styles.round}`}
                      ></span>
                    </label>
                    {activeInsurance ? "Ativado" : "Desativado"}
                  </div>
                </div>
              </div>
              <div className={styles.buttonWrapper}>
                <button
                  type="submit"
                  onClick={sendData}
                  className={styles.button}
                  disabled={(!apiKey || !intelipostApiKey) || !(sedexCode || pacCode || pacminiCode || ownDeliveryCode) || !validatedAddressError() || invalidCnpjs.length > 0}
                >
                  Salvar
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export { Intelipost };
